import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Grid,
  Skeleton,
  Fade
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  Article as ArticleIcon,
  Visibility as ViewIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { eventsService } from '../services/api';
import { resultsService } from '../services/api'; 

const LatestResults = () => {
  const navigate = useNavigate();
  const [latestResult, setLatestResult] = useState(null);
  const [resultDetails, setResultDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const hasLoadedRef = useRef(false);

  // const loadLatestResults = useCallback(async () => {
  //   try {
  //     setLoading(true);
  //     setError('');
      
  //     // Get closed events
  //     const response = await eventsService.getEvents({ status: 'closed' });
  //     const apiData = response.data?.data || response.data;
  //     const closedEvents = apiData?.events || [];
      
  //     // Filter for events with published results, exclude archived events, and sort by publication date
  //     const eventsWithResults = closedEvents
  //       .filter(event => event.results_published && event.status !== 'archived')
  //       .sort((a, b) => new Date(b.results_published_at) - new Date(a.results_published_at));
      
  //     if (eventsWithResults.length > 0) {
  //       const latestEvent = eventsWithResults[0];
  //       setLatestResult(latestEvent);
        
  //       // Fetch detailed results for the latest event
  //       try {
  //         const resultsResponse = await eventsService.getEventResults(latestEvent.event_id);
  //         const resultsApiData = resultsResponse.data?.data || resultsResponse.data;
  //         setResultDetails(resultsApiData);
  //       } catch (resultsErr) {
  //         console.error('Error loading detailed results:', resultsErr);
  //         // Continue without detailed results
  //       }
  //     }
      
  //   } catch (err) {
  //     console.error('Error loading latest results:', err);
  //     setError('Failed to load latest results');
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []); // Empty dependency array to make function stable

  // useEffect(() => {
  //   if (!hasLoadedRef.current) {
  //     hasLoadedRef.current = true;
  //     loadLatestResults();
  //   }
  // }, [loadLatestResults]);
const loadLatestResults = useCallback(async () => {
  try {
    setLoading(true);
    setError('');
    const response = await resultsService.getLatestResultSnapshot();
    const latest = response.data?.data || null;
    
    if (latest && latest.event_id) {
      setLatestResult({
        event_id: latest.event_id,
        title: latest.title || 'Contest Results',
        description: latest.description || '',
        theme: latest.theme || '',
        start_date: latest.start_date || '',
        end_date: latest.end_date || '',
        status: latest.status || '',
        results_published_at: latest.results_published_at,
        participant_count: latest.summary?.participant_count || 0,
        submission_count: latest.summary?.total_submissions || 0,
      });
      setResultDetails({
        winner: latest.winner,
        finalists: latest.finalists || [],
      });
    } else {
      setLatestResult(null);
      setResultDetails(null);
    }
  } catch (err) {
    setError('Failed to load latest results');
  } finally {
    setLoading(false);
  }
}, []);

useEffect(() => {
  if (!hasLoadedRef.current) {
    hasLoadedRef.current = true;
    loadLatestResults();
  }  
}, [loadLatestResults]);
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const publishedDate = new Date(dateString);
    const diffInHours = Math.floor((now - publishedDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return formatDate(dateString);
  };

  if (loading) {
    return (
      <Box sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Skeleton variant="text" width={300} height={40} sx={{ mx: 'auto', mb: 1 }} />
          <Skeleton variant="text" width={200} height={24} sx={{ mx: 'auto' }} />
        </Box>
        <Card sx={{ maxWidth: 800, mx: 'auto' }}>
          <CardContent sx={{ p: 4 }}>
            <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="40%" height={24} sx={{ mb: 3 }} />
            <Skeleton variant="text" width="80%" height={28} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="70%" height={28} sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Skeleton variant="text" width={120} height={24} />
              <Skeleton variant="text" width={100} height={24} />
            </Box>
            <Skeleton variant="rectangular" width={140} height={36} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (error || !latestResult) {
    return null; // Don't show anything if there's an error or no results
  }

  return (
    <Fade in timeout={600}>
      <Box sx={{ py: 6, bgcolor: 'grey.50' }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <TrophyIcon sx={{ color: '#667eea', fontSize: 'inherit' }} />
            Latest Contest Results
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Celebrating our talented writers and their amazing stories
          </Typography>
        </Box>

        {/* Results Card */}
        <Card 
          sx={{ 
            maxWidth: 800, 
            mx: 'auto',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: 6
            },
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'primary.light'
          }}
          onClick={() => navigate(`/events/${latestResult.event_id}/results`)}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Contest Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                  🎯 {latestResult.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {latestResult.theme && (
                    <Chip 
                      label={latestResult.theme} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(latestResult.start_date)} - {formatDate(latestResult.end_date)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Chip 
                label={getTimeAgo(latestResult.results_published_at)}
                color="success"
                size="small"
                icon={<StarIcon />}
                sx={{ fontWeight: 'bold' }}
              />
            </Box>

            {/* Winner & Finalist Info */}
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={3}>
                {/* Winner */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'rgba(255, 215, 0, 0.1)', 
                    borderRadius: 2,
                    border: '1px solid rgba(255, 215, 0, 0.3)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TrophyIcon sx={{ color: '#FFD700', mr: 1 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Winner
                      </Typography>
                    </Box>
                    {resultDetails?.winner ? (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                          "{resultDetails.winner.story_title}"
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          by {resultDetails.winner.user_name}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          🏆 Contest winner announced
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Click to see winner details
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>

                {/* Top Finalist */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'rgba(192, 192, 192, 0.1)', 
                    borderRadius: 2,
                    border: '1px solid rgba(192, 192, 192, 0.3)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: '50%', 
                        bgcolor: '#C0C0C0', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        mr: 1
                      }}>
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
                          2
                        </Typography>
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Finalist
                      </Typography>
                    </Box>
                    {resultDetails?.finalists && resultDetails.finalists.length > 0 ? (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                          "{resultDetails.finalists[0].story_title}"
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          by {resultDetails.finalists[0].user_name}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          🥈 Top finalists selected
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          See all rankings and stories
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Contest Stats & Action */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PeopleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {latestResult.participant_count || 0} participants
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ArticleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {latestResult.submission_count || 0} submissions
                  </Typography>
                </Box>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<ViewIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/events/${latestResult.event_id}/results`);
                }}
                sx={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  }
                }}
              >
                View Full Results
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
};

export default LatestResults;
